import { useState, useEffect, useCallback } from 'react';
import apiClient from '../services/apiClient';

import { processDatasetRawData, STRICT_CONFIG_MASTER, STRICT_CONFIG_USER } from '../utils/datasetUtils';

// Global in-memory cache (persists during navigation, cleared on refresh)
let globalHookCache = null;

const useDatasetData = (sourceId, versions = []) => {
    const [data, setData] = useState([]); // Raw data (if needed)
    const [flattenedData, setFlattenedData] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [mainCache, setMainCache] = useState([]); // Cache for main dataset to avoid refetching
    const [mainRawCache, setMainRawCache] = useState([]); // Cache for raw main dataset JSON
    const [metadata, setMetadata] = useState({}); // Store version metadata (like filterConfig)

    // Helper wrapper for utils
    const processData = (raw, config) => processDatasetRawData(raw, config);

    const fetchMainDataset = async (forceRefresh = false) => {
        // 1. Check in-memory cache first
        if (!forceRefresh && mainCache && mainCache.length > 0) {
            setData(mainRawCache);
            return mainCache;
        }


        // 2. Check global in-memory cache
        if (!forceRefresh && globalHookCache) {
            const { raw, processed } = globalHookCache;
            if (Array.isArray(processed) && Array.isArray(raw)) {
                setMainCache(processed);
                setMainRawCache(raw);
                setData(raw);
                return processed;
            }
        }

        // 3. Fetch from API
        try {
            const response = await apiClient.get('/cache/download/json');
            if (response.ok) {
                const jsonData = await response.json();
                let raw = [];
                if (Array.isArray(jsonData)) raw = jsonData;
                else if (jsonData && Array.isArray(jsonData.data)) raw = jsonData.data;

                const processed = processData(raw, STRICT_CONFIG_MASTER);

                setMainCache(processed);
                setMainRawCache(raw);
                setData(raw);

                // Update global in-memory cache
                globalHookCache = { raw, processed };

                return processed;
            } else {
                setData([]);
                return [];
            }
        } catch (err) {
            console.error(err);
            setError("Error loading data.");
            setData([]);
            return [];
        }

    };

    const fetchPendingDataset = async () => {
        try {
            const res = await apiClient.get('/data/pending');
            if (res.ok) {
                const data = await res.json();
                let raw = [];
                if (Array.isArray(data)) raw = data;
                else if (data && Array.isArray(data.data)) raw = data.data;

                if (raw.length > 0) {
                    const processed = processData(raw, STRICT_CONFIG_USER);
                    return { raw: raw, processed };
                }
            }
        } catch (ignored) { }
        return { raw: [], processed: [] };
    };

    const fetchMySubmissions = async () => {
        try {
            const res = await apiClient.get('/data/my-submissions');
            if (res.ok) {
                const submissions = await res.json();
                if (submissions && submissions.length > 0) {
                    const allExperiments = submissions.flatMap(sub => sub.experimentData || []);
                    if (allExperiments.length > 0) {
                        const processed = processData(allExperiments, STRICT_CONFIG_USER);
                        return { raw: allExperiments, processed };
                    }
                }
            }
        } catch (ignored) { }
        return { raw: [], processed: [] };
    };

    const processVersionData = useCallback((version) => {
        if (version && version.datasetSnapshot) {
            try {
                const snapshotData = typeof version.datasetSnapshot === 'string'
                    ? JSON.parse(version.datasetSnapshot)
                    : version.datasetSnapshot;

                let rawData = [];
                if (Array.isArray(snapshotData)) {
                    rawData = snapshotData;
                } else if (snapshotData && Array.isArray(snapshotData.data)) {
                    rawData = snapshotData.data;
                }

                if (rawData.length > 0) {
                    // Only camelCase `experimentId` reliably marks *raw* (unflattened) data.
                    // `recordId` is not a valid signal here: processDatasetRawData stamps a
                    // lowercase `recordId` onto its own flattened output too, so checking for
                    // it caused already-flattened snapshots (e.g. from Save Workspace) to be
                    // re-run through the flattener, which then failed to match any column
                    // pattern against the human-readable headers and produced an all-'-' table.
                    if (rawData[0] && rawData[0].hasOwnProperty('experimentId')) {
                        const processed = processData(rawData, STRICT_CONFIG_MASTER);
                        setFlattenedData(processed);
                    } else {
                        setFlattenedData(rawData);
                    }
                } else {
                    setFlattenedData([]);
                }

                let filterConfig = {};
                if (version.filterConfig) {
                    try {
                        filterConfig = typeof version.filterConfig === 'string'
                            ? JSON.parse(version.filterConfig)
                            : version.filterConfig;
                    } catch (e) {
                        console.error("Error parsing filter config", e);
                    }
                }
                setMetadata({
                    id: version.id,
                    name: version.name,
                    isMain: false,
                    isPending: !!version.isProvisional,
                    filterConfig: filterConfig,
                    datasetSnapshot: rawData
                });

            } catch (e) {
                console.error("JSON parse error for snapshot", e);
                setError("Failed to parse version data.");
                setFlattenedData([]);
            }
        } else {
            setError("Version data not found.");
            setFlattenedData([]);
        }
    }, []);

    const load = useCallback(async (forceRefresh = false) => {
        setLoading(true);
        setError(null);
        setMetadata({});

        if (sourceId === 'SKIP') {
            setLoading(false);
            return;
        }

        if (!sourceId || sourceId === 'main') {
            const data = await fetchMainDataset(forceRefresh);
            setFlattenedData(data);
            setMetadata({ name: "Master Dataset", isMain: true });
            setLoading(false);
        } else if (sourceId === 'pending') {
            const { raw, processed } = await fetchPendingDataset();
            setData(raw);
            setFlattenedData(processed);
            setMetadata({ name: "Provisional Data", isMain: false, isPending: true });
            setLoading(false);
        } else if (sourceId === 'my-submissions') {
            const { raw, processed } = await fetchMySubmissions();
            setData(raw);
            setFlattenedData(processed);
            setMetadata({ name: "My Submissions", isMain: false, isPending: true });
            setLoading(false);
        } else {
            const preloadedVersion = versions.find(v => v.id === sourceId);

            if (preloadedVersion && preloadedVersion.datasetSnapshot) {
                processVersionData(preloadedVersion);
                setLoading(false);
            } else {
                try {
                    const res = await apiClient.get(`/versions/${sourceId}`);
                    if (!res.ok) throw new Error("Failed to load version details");
                    const version = await res.json();
                    processVersionData(version);
                } catch (err) {
                    console.error("Error fetching version:", err);
                    setError("Failed to fetch version data.");
                    setFlattenedData([]);
                } finally {
                    setLoading(false);
                }
            }
        }
    }, [sourceId, versions, processVersionData]);

    useEffect(() => {
        load();
    }, [load]);

    return { flattenedData, data, loading, error, metadata, refresh: () => load(true) };
};

export default useDatasetData;
